/**
 * @file Configuration setting keys mirrored from backend settings XML.
 * Keep this file in sync with:
 * - utrade-backend/src/_platforms/_admin/storage/settings.xml
 * - utrade-backend/src/_platforms/_product/utils/configurationSettingKeys.js
 *
 * Maintenance rule:
 * - After adding/removing/renaming a key, backend must also update
 *   utrade-backend/src/_platforms/_admin/externals/AdminServicesProvider.js
 *   -> GLOBAL_CONFIGURATION_CASTS_BY_KEY with the correct cast type
 *   (raw|number|int|boolean|csv), otherwise global config session values may be mismatched.
 *
 * @module _platforms/_product/utils/configurationSettingKeys
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.9
 * @version 2.1.2
 */
export const CONFIGURATION_SETTING_KEYS = {
  KEY__ENABLE_POSTHOG: 'enablePosthog',
  KEY__ENABLE_UNIFIED_INVENTORY_GATEWAY: 'enableUnifiedInventoryGateway',
  KEY__AUTO_ROUTE_PLANNING_WAREHOUSE_IDS: 'autoRoutePlanningWarehouseIds',
  KEY__AUTO_ROUTE_PLANNING_VEHICLE_LIMIT_PERCENT:
    'autoRoutePlanningVehicleLimitPercent',
  KEY__AUTO_ROUTE_AFTER_LAST_DELIVERY_ENABLED:
    'autoRouteAfterLastDeliveryEnabled',
  KEY__AUTO_ROUTE_NEXT_DISPATCH_TIME: 'autoRouteNextDispatchTime',
  KEY__AUTO_ROUTE_QUICK_PLAN_RADIUS_KM: 'autoRouteQuickPlanRadiusKm',
  KEY__ROUTE_PLANNING_VEHICLE_LOADING_TIME_HOURS:
    'routePlanningVehicleLoadingTimeHours',
  KEY__ROUTE_PLANNING_VEHICLE_TOTAL_BUSINESS_HOURS:
    'routePlanningVehicleTotalBusinessHours',
  KEY__ROUTE_PLANNING_EACH_ORDER_DELIVERY_TIME_MINUTES:
    'routePlanningEachOrderDeliveryTimeMinutes',
  KEY__ROUTE_PLANNING_MAX_ORDERS_PER_VEHICLE_LIMIT:
    'routePlanningMaxOrdersPerVehicleLimit',
  KEY__ROUTE_PLANNING_LOADING_MANAGER_ROLE_IDS:
    'routePlanningLoadingManagerRoleIds',
  KEY__ROUTE_PLANNING_LOADING_MANAGER_ROUND_ROBIN_INDEX:
    'routePlanningLoadingManagerRoundRobinIndex',
  KEY__AUTO_ROUTE_PLANNING_CRON_EXPRESSION:
    'autoRoutePlanningCronExpression',
  KEY__AUTO_ROUTE_PLANNING_NOTIFICATION_EMAILS:
    'autoRoutePlanningNotificationEmails',
  KEY__CLEAR_ROUTE_PLAN_QUEUE: 'clearRoutePlanQueue',
  KEY__FREE_ORDER: 'freeOrder',
  KEY__DELIVER_ORDER: 'deliverOrder',
  KEY__CANCEL_ORDER: 'cancelOrder',
  KEY__DOWNLOAD_DEBUG_LOGS_ZIP: 'downloadDebugLogsZip',
  KEY__IGNORE_CACHE_FOR_INVENTORY: 'ignorecacheforinventory',
  KEY__CLEAR_DEBUG_LOGS: 'clearDebugLogs',
  KEY__SYNC_DATABASE_INDEXES: 'syncDatabaseIndexes',
  KEY__SYNC_ALL_DATABASE_INDEXES: 'syncAllDatabaseIndexes',
  KEY__DEBUG: 'debug',
  KEY__HEALTHY_GROSS_PROFIT_THRESHOLD: 'healthyGrossProfitThreshold',
  KEY__MINIMUM_GROSS_PROFIT_THRESHOLD: 'minimumGrossProfitThreshold',
  KEY__DEFAULT_SUPPLIER_LEAD_TIME_DAYS: 'defaultSupplierLeadTimeDays',
  KEY__DOIH_EXTRA_DAYS_OVER_LEAD_TIME: 'doihExtraDaysOverLeadTime',
  KEY__DRAFT_PO_RUN_TIME: 'draftPoRunTime',
  KEY__DRAFT_PO_ATS_RANGE_DAYS: 'draftPoAtsRangeDays',
  KEY_PAYMENT_OPTIONS: 'PaymentOptions',
  KEY__NOTIFICATION_DEFAULT_TYPE: 'notificationDefaultType',
  KEY__NOTIFICATION_SWAL_BASIC_TYPE: 'notificationSwalBasicType',
  KEY__NOTIFICATION_SWAL_BASIC_TYPE_WITH_DEFAULT_SOUND:
    'notificationSwalBasicTypeWithDefaultSound',
  KEY__NOTIFICATION_DEFAULT_SOUND: 'notificationDefaultSound',
  KEY__NOTIFICATION_AUTO_ROUTE_PLAN: 'notificationAutoRoutePlan',
  KEY__NOTIFICATION_LAST_ORDER_DELIVERED: 'notificationLastOrderDelivered',
  KEY__SERVICE_REQUEST_ACCESS_ROLE_IDS: 'serviceRequestAccessRoleIds',
  KEY__SERVICE_REQUEST_FREE_ORDER_REQUESTER_ROLE_IDS:
    'serviceRequestFreeOrderRequesterRoleIds',
  KEY__SERVICE_REQUEST_FREE_ORDER_APPROVER_ROLE_IDS:
    'serviceRequestFreeOrderApproverRoleIds',
  KEY__SERVICE_REQUEST_DELIVER_ORDER_REQUESTER_ROLE_IDS:
    'serviceRequestDeliverOrderRequesterRoleIds',
  KEY__SERVICE_REQUEST_DELIVER_ORDER_APPROVER_ROLE_IDS:
    'serviceRequestDeliverOrderApproverRoleIds',
  KEY__SERVICE_REQUEST_CANCEL_ORDER_REQUESTER_ROLE_IDS:
    'serviceRequestCancelOrderRequesterRoleIds',
  KEY__SERVICE_REQUEST_CANCEL_ORDER_APPROVER_ROLE_IDS:
    'serviceRequestCancelOrderApproverRoleIds',
  KEY__NOTIFICATION_PEOPLE_CHAT: 'notificationPeopleChat',
  KEY__PEOPLE_CHAT_ROLE_IDS: 'peopleChatRoleIds',
  KEY__PEOPLE_CHAT_ALLOWED_EXTENSIONS: 'peopleChatAllowedExtensions',
} as const;

export type ConfigurationSettingKey =
  (typeof CONFIGURATION_SETTING_KEYS)[keyof typeof CONFIGURATION_SETTING_KEYS];
